import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Option } from '../types';

export interface MultiSelectProps {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  error?: string;
  fieldRef?: (node: HTMLElement | null) => void;
  'aria-label'?: string;
}

export default function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  loading = false,
  disabled = false,
  error,
  fieldRef,
  'aria-label': ariaLabel,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const selected = value.map(String);
  const selectedLabels = selected
    .map((selectedValue) =>
      options.find((option) => String(option.value) === selectedValue)
    )
    .filter(Boolean) as Option[];

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        ref={(node) => fieldRef?.(node)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={!!error}
        aria-label={ariaLabel}
        onClick={() => setOpen((current) => !current)}
        className={`flex min-h-11 w-full items-center gap-2 rounded-lg border bg-white px-3 py-2 text-left text-[13px] shadow-sm outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100 disabled:cursor-not-allowed disabled:bg-slate-50 ${
          error ? 'border-red-400' : 'border-slate-200'
        }`}
      >
        <span className="min-w-0 flex-1">
          {selectedLabels.length ? (
            <span className="flex flex-wrap gap-1">
              {selectedLabels.slice(0, 3).map((option) => (
                <span
                  key={option.value}
                  className="rounded-md bg-primary-50 px-1.5 py-0.5 text-[10px] font-medium text-primary-700"
                >
                  {option.label}
                </span>
              ))}
              {selectedLabels.length > 3 && (
                <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
                  +{selectedLabels.length - 3} more
                </span>
              )}
            </span>
          ) : (
            <span className="text-slate-400">
              {loading ? 'Loading options…' : placeholder}
            </span>
          )}
        </span>

        <ChevronDown
          className={`h-4 w-4 shrink-0 text-slate-400 transition ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 z-50 mt-1.5 max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white p-1.5 shadow-lg"
          role="listbox"
          aria-multiselectable="true"
          aria-label={ariaLabel}
        >
          {loading ? (
            <OptionSkeleton />
          ) : options.length ? (
            options.map((option) => {
              const checked = selected.includes(String(option.value));

              return (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-[12px] text-slate-700 hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      onChange(
                        checked
                          ? selected.filter(
                              (item) => item !== String(option.value)
                            )
                          : [...selected, String(option.value)]
                      )
                    }
                    disabled={disabled}
                    className="h-3.5 w-3.5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="min-w-0 flex-1 truncate">
                    {option.label}
                  </span>
                  {checked && (
                    <Check className="h-3.5 w-3.5 text-primary-600" />
                  )}
                </label>
              );
            })
          ) : (
            <div className="px-2.5 py-5 text-center text-[11px] text-slate-400">
              No options available.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function OptionSkeleton() {
  return (
    <div className="space-y-1.5 p-1">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="flex items-center gap-2 px-2 py-2">
          <div className="h-3.5 w-3.5 animate-pulse rounded bg-slate-200" />
          <div className="h-3 w-3/4 animate-pulse rounded bg-slate-200" />
        </div>
      ))}
    </div>
  );
}
